<script setup lang="ts">
import type { Radio } from '~/types'

import { ref, computed } from 'vue'
import { useStore } from '~/store'
import { useI18n } from 'vue-i18n'

import UserLink from '~/components/common/UserLink.vue'

import RadioButton from './Button.vue'
import Button from '~/ui/Button.vue'
import Card from '~/ui/Card.vue'
import Link from '~/ui/Link.vue'
import Spacer from '~/ui/Spacer.vue'
import { to } from '~/routing'

interface Props {
  type: string
  customRadio?: Radio | null
  objectId?: string | null
}

const { t } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  customRadio: null,
  objectId: null
})

const store = useStore()

const isDescriptionExpanded = ref(false)

const radio = computed(() => props.customRadio
  ? props.customRadio
  : store.getters['radios/types'][props.type]
)

const customRadioId = computed(() => props.customRadio?.id ?? null)
</script>

<template>
<!-- TODO: Separate view from functionality. Move RadioButton logic into a composable so that it can be styled directly -->

  <Card
    v-if="radio.id"
    class="color-blue-solid"
    :title="radio.name"
    v-bind="to({name: 'library.radios.detail', params: {id: radio.id}})"
  >
    <template #topright>
      <RadioButton
        :type="type"
        :custom-radio-id="customRadioId"
        :object-id="objectId"
        :radioConfig="customRadio?.config"
        play-only
      />
    </template>

    <template #default>
      <Spacer size-16 />
      <UserLink
        v-if="radio.user"
        :user="radio.user"
        avatar
      />
      <Spacer />
      <div
        class="description"
        :class="{expanded: isDescriptionExpanded}"
        @click="isDescriptionExpanded = !isDescriptionExpanded"
      >
        {{ radio.description }}
      </div>
    </template>

    <template #action>
      <Link
        v-if="store.state.auth.authenticated && type === 'custom' && radio.user.id === store.state.auth.profile?.id"
        class="color-primary-solid shape-field-grow"
        ui-raised
        ui-no-underline
        v-bind="to({name: 'library.radios.edit', params: {id: customRadioId }})"
      >
      <Icon icon="bi:pencil-square" />
        {{ t('components.radios.Card.button.edit') }}
      </Link>
    </template>
  </Card>
  <Card
    v-else
    class="color-blue-solid"
    icon="bi:boombox-fill"
    :title="radio.name"
  >
    <template #default>
      <UserLink
        v-if="radio.user"
        :user="radio.user"
        :avatar="false"
      />
      <Spacer v-if="radio.user" />
      <div
        class="description"
        :class="{expanded: isDescriptionExpanded}"
        @click="isDescriptionExpanded = !isDescriptionExpanded"
      >
        {{ radio.description }}
      </div>
    </template>

    <template #action>
      <RadioButton
        :type="type"
        :custom-radio-id="customRadioId"
        :object-id="objectId"
        class="shape-field-grow"
      />
      <Button
        v-if="store.state.auth.authenticated && type === 'custom' && radio.user.id === store.state.auth.profile?.id"
        :to="{name: 'library.radios.edit', params: {id: customRadioId }}"
      >
      <Icon icon="bi:pencil-square" />
        {{ t('components.radios.Card.button.edit') }}
      </Button>
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.play-button.is-icon-only {
  top: 16px;
  right: 16px;
}
h6.title {
  font-size: 3em;
}
</style>
